import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Send, Bot, User, Loader2, Cpu, FileText, BookOpen, Paperclip, X } from "lucide-react";
import AttachDocumentsModal, { type AttachedDoc } from "./AttachDocumentsModal";
import { Link } from "react-router-dom";
import {
  listAllEnabledModels,
  chatWithConnection,
  type EnabledModelEntry,
  type ChatMessageIn,
  type ChatSourceRef,
} from "@/api/aiConnections";

interface UIMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  error?: boolean;
  sources?: ChatSourceRef[];
}

export default function ConnectionChatInterface() {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [useDocuments, setUseDocuments] = useState(true);
  const [attachedDocs, setAttachedDocs] = useState<AttachedDoc[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: models = [], isLoading: modelsLoading } = useQuery<
    EnabledModelEntry[]
  >({
    queryKey: ["ai-enabled-models"],
    queryFn: listAllEnabledModels,
  });

  // Default pick: first available model
  useEffect(() => {
    if (!selectedKey && models.length > 0) {
      const first = models[0];
      setSelectedKey(`${first.connection_id}::${first.model}`);
    }
  }, [models, selectedKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selected = models.find(
    (m) => `${m.connection_id}::${m.model}` === selectedKey,
  );

  const handleSend = async () => {
    if (!input.trim() || isLoading || !selected) return;
    const question = input.trim();
    setInput("");
    setIsLoading(true);

    const userMsg: UIMessage = {
      role: "user",
      content: question,
      timestamp: new Date().toISOString(),
    };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);

    // Build chat history for API
    const apiMessages: ChatMessageIn[] = nextMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const res = await chatWithConnection(
        selected.connection_id,
        selected.model,
        apiMessages,
        useDocuments,
        attachedDocs.map((d) => d.id),
      );
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: res.content || "(kosong)",
          timestamp: new Date().toISOString(),
          sources: res.sources,
        },
      ]);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            err?.response?.data?.detail ??
            "Terjadi kesalahan saat menghubungi model.",
          timestamp: new Date().toISOString(),
          error: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header: model selector */}
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600">
          <Bot size={16} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800">Asisten AI</p>
          <p className="truncate text-xs text-gray-400">
            {selected
              ? `${selected.provider} • ${selected.connection_name}`
              : "Pilih model untuk memulai"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 text-gray-400" />
          <select
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            disabled={modelsLoading || models.length === 0}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-mono"
          >
            {models.length === 0 && (
              <option value="">Tidak ada model aktif</option>
            )}
            {models.map((m) => {
              const key = `${m.connection_id}::${m.model}`;
              return (
                <option key={key} value={key}>
                  {m.provider} / {m.model}
                </option>
              );
            })}
          </select>
        </div>

        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={useDocuments}
            onChange={(e) => setUseDocuments(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <BookOpen className="h-3.5 w-3.5" />
          Gunakan dokumen
        </label>

        {messages.length > 0 && (
          <button
            onClick={() => setMessages([])}
            className="text-xs text-gray-400 hover:text-red-500"
          >
            Sesi baru
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {models.length === 0 && !modelsLoading && (
          <div className="mt-16 text-center text-sm text-gray-400">
            Belum ada model aktif. Admin perlu menambahkan koneksi dan
            mengaktifkan model di halaman Pengaturan.
          </div>
        )}

        {messages.length === 0 && models.length > 0 && (
          <div className="mt-16 text-center text-gray-400">
            <Bot size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Mulai percakapan dengan model yang dipilih</p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex gap-3 ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {msg.role === "assistant" && (
              <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-600">
                <Bot size={13} className="text-white" />
              </div>
            )}
            <div className="max-w-[80%]">
              <div
                className={`rounded-2xl px-4 py-2.5 text-sm ${
                  msg.role === "user"
                    ? "rounded-br-sm bg-blue-600 text-white"
                    : msg.error
                    ? "rounded-bl-sm bg-red-50 text-red-800"
                    : "rounded-bl-sm bg-gray-100 text-gray-800"
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
              {msg.role === "assistant" && msg.sources && msg.sources.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500">
                    {msg.sources.length} sumber dokumen
                  </p>
                  {msg.sources.map((s, i) => (
                    <Link
                      key={i}
                      to={`/documents/${s.doc_id}`}
                      className="block rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 hover:bg-blue-100"
                    >
                      <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                        <FileText className="h-3 w-3" />
                        <span className="truncate">
                          [{i + 1}] {s.judul ?? s.doc_id}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-gray-600">
                        {s.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {msg.role === "user" && (
              <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
                <User size={13} className="text-gray-600" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3">
            <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-600">
              <Bot size={13} className="text-white" />
            </div>
            <div className="rounded-2xl rounded-bl-sm bg-gray-100 px-4 py-2.5">
              <Loader2 size={14} className="animate-spin text-gray-500" />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 p-3">
        {attachedDocs.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {attachedDocs.map((d) => (
              <span
                key={d.id}
                className="flex max-w-xs items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 py-1 pl-2 pr-1 text-xs text-blue-800"
              >
                <FileText className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{d.judul}</span>
                <button
                  type="button"
                  onClick={() =>
                    setAttachedDocs((prev) => prev.filter((x) => x.id !== d.id))
                  }
                  className="rounded-full p-0.5 hover:bg-blue-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            title="Lampirkan dokumen"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-blue-600"
          >
            <Paperclip size={16} />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              selected
                ? "Ketik pesan... (Enter untuk kirim, Shift+Enter baris baru)"
                : "Pilih model terlebih dahulu"
            }
            rows={1}
            disabled={!selected || isLoading}
            className="max-h-32 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading || !selected}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700 disabled:opacity-40"
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>

      {showPicker && (
        <AttachDocumentsModal
          selected={attachedDocs}
          onClose={() => setShowPicker(false)}
          onSave={(docs) => {
            setAttachedDocs(docs);
            setShowPicker(false);
          }}
        />
      )}
    </div>
  );
}
